import React, { useState, useEffect, useCallback } from 'react';
import API from '../../services/api';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const BikeLifeCycleView = ({ bikeId, onClose }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('summary');

    const fetchHistory = useCallback(async () => {
        try {
            setLoading(true);
            const cleanId = String(bikeId).split(':')[0];
            const res = await API.get(`/v1/bikes/lifecycle/${cleanId}`);
            if (res.data.success) setData(res.data.data);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    }, [bikeId]);

    useEffect(() => { fetchHistory(); }, [fetchHistory]);

    const generateProfessionalPDF = () => {
        const { bike_info, inventory, sales, registration_history } = data;
        const doc = new jsPDF();
        
        // --- Header Section ---
        doc.setFillColor(15, 23, 42); 
        doc.rect(0, 0, 210, 45, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(24).setFont("helvetica", "bold").text('UNITED MOTORS', 105, 20, { align: 'center' });
        doc.setFontSize(10).setFont("helvetica", "normal").text('COMPLETE VEHICLE LIFECYCLE & OWNERSHIP AUDIT', 105, 28, { align: 'center' });
        doc.setFontSize(8).text(`Report Generated: ${new Date().toLocaleString()}`, 105, 36, { align: 'center' });

        // --- 1. Vehicle Identity (Full Details) ---
        doc.setTextColor(30, 41, 59);
        doc.setFontSize(14).setFont("helvetica", "bold").text('1. VEHICLE SPECIFICATIONS', 14, 58);
        doc.setDrawColor(226, 232, 240).line(14, 60, 196, 60);

        autoTable(doc, {
            startY: 63,
            theme: 'plain',
            styles: { fontSize: 9, cellPadding: 2 },
            columnStyles: { 0: { fontStyle: 'bold', width: 40 }, 2: { fontStyle: 'bold', width: 40 } },
            body: [
                ['Model Name:', bike_info.model_name, 'Model Year:', bike_info.model_year],
                ['Engine No:', bike_info.engine_number, 'Chassis No:', bike_info.chassis_number],
                ['Color:', bike_info.color, 'Current Status:', bike_info.stock_status || 'In Stock']
            ]
        });

        // --- 2. Sales & Ownership History (Blue) ---
        doc.setFontSize(12).setFont("helvetica", "bold").text('2. OWNERSHIP & SALES HISTORY', 14, doc.lastAutoTable.finalY + 10);
        autoTable(doc, {
            startY: doc.lastAutoTable.finalY + 13,
            theme: 'grid',
            headStyles: { fillColor: [37, 99, 235] },
            head: [['Owner #', 'Date', 'Customer Name', 'Sale Type', 'Net Amount']],
            body: sales.map((s, i) => [
                `Owner ${sales.length - i}`,
                new Date(s.sale_date).toLocaleDateString(),
                s.full_name.toUpperCase(),
                s.sale_type,
                `Rs. ${Math.round(s.net_amount).toLocaleString()}`
            ]),
        });

        // --- 3. Inventory & Purchase Log (Green) ---
        doc.setFontSize(12).setFont("helvetica", "bold").text('3. STOCK & ACQUISITION HISTORY', 14, doc.lastAutoTable.finalY + 10);
        autoTable(doc, {
            startY: doc.lastAutoTable.finalY + 13,
            theme: 'grid',
            headStyles: { fillColor: [16, 185, 129] },
            head: [['Entry Date', 'Type', 'Source / Supplier', 'Purchase Price']],
            body: inventory.map(item => [
                new Date(item.purchase_date).toLocaleDateString(),
                item.purchase_type,
                (item.supplier_name || item.buyback_name || 'INITIAL STOCK').toUpperCase(),
                `Rs. ${Math.round(item.purchase_price || item.purchase_cost || 0).toLocaleString()}`
            ]),
        });

        // --- 4. Registration History (Slate) ---
        doc.setFontSize(12).setFont("helvetica", "bold").text('4. REGISTRATION & PLATE LOG', 14, doc.lastAutoTable.finalY + 10);
        autoTable(doc, {
            startY: doc.lastAutoTable.finalY + 13,
            theme: 'grid',
            headStyles: { fillColor: [71, 85, 105] },
            head: [['Plate Number', 'Assigned Date', 'Released Date', 'Current Status']],
            body: registration_history.map(r => [
                r.reg_number,
                new Date(r.assigned_date).toLocaleDateString(),
                r.released_date ? new Date(r.released_date).toLocaleDateString() : 'Active',
                r.is_current ? 'CURRENT' : 'PREVIOUS'
            ]),
        });

        // Final Footer
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setFontSize(8).setTextColor(150).text(`United Motors - Page ${i} of ${pageCount} | Engine: ${bike_info.engine_number}`, 105, 290, { align: 'center' });
        }

        doc.save(`Complete_History_${bike_info.engine_number}.pdf`);
    };

    if (loading) return <div style={styles.overlay}><div style={styles.loader}>🔄 <br/> Loading Full Audit Data...</div></div>;
    const { bike_info, inventory, sales, registration_history } = data;
    const currentPlate = registration_history.find(r => r.is_current)?.reg_number || 'APPLIED';

    return (
        <div style={styles.overlay}>
            <div style={styles.modal}>
                {/* Header */}
                <div style={styles.topHeader}>
                    <div style={{display:'flex', alignItems:'center', gap:'15px'}}>
                        <div style={styles.logoBadge}>UM</div>
                        <div>
                            <h2 style={styles.logo}>UNITED MOTORS</h2>
                            <span style={styles.tagline}>INTELLIGENT HISTORY TRACKER</span>
                        </div>
                    </div>
                    <div style={{display:'flex', gap:'10px'}}>
                        <button onClick={generateProfessionalPDF} style={styles.downloadBtn}>📥 EXPORT COMPLETE HISTORY</button>
                        <button onClick={onClose} style={styles.exitBtn}>&times;</button>
                    </div>
                </div>

                {/* Sub-Header Stats */}
                <div style={styles.statusStrip}>
                    <div style={styles.statusItem}><label>ENGINE NO</label><b>{bike_info.engine_number}</b></div>
                    <div style={styles.statusItem}><label>REGISTRATION</label><b style={{color:'#2563eb'}}>{currentPlate}</b></div>
                    <div style={styles.statusItem}><label>STATUS</label><b style={{color:'#10b981'}}>{bike_info.stock_status}</b></div>
                </div>

                {/* Tab Navigation */}
                <div style={styles.tabNav}>
                    {['summary', 'sales', 'inventory', 'registration'].map(t => (
                        <button key={t} onClick={() => setActiveTab(t)} 
                        style={{...styles.tab, 
                        borderBottom: activeTab === t ? `4px solid ${t === 'sales' ? '#2563eb' : t === 'inventory' ? '#10b981' : '#475569'}` : 'none',
                        color: activeTab === t ? '#1e293b' : '#94a3b8'}}>
                            {t.toUpperCase()}
                        </button>
                    ))}
                </div>

                {/* Main Content Area */}
                <div style={styles.scrollContent}>
                    {activeTab === 'summary' && (
                        <div style={styles.grid}>
                            <div style={{...styles.card, borderLeft:'5px solid #475569'}}>
                                <h4>Technical Identity</h4>
                                <div style={styles.dataRow}><span>Model</span> <b>{bike_info.model_name}</b></div>
                                <div style={styles.dataRow}><span>Year</span> <b>{bike_info.model_year}</b></div>
                                <div style={styles.dataRow}><span>Chassis</span> <b>{bike_info.chassis_number}</b></div>
                            </div>
                            <div style={{...styles.card, borderLeft:'5px solid #2563eb'}}>
                                <h4>Ownership Summary</h4>
                                <div style={styles.dataRow}><span>Total Owners</span> <b>{sales.length}</b></div>
                                <div style={styles.dataRow}><span>Last Sale</span> <b>{sales[0] ? new Date(sales[0].sale_date).toLocaleDateString() : 'N/A'}</b></div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'sales' && (
                        <div style={styles.tableBox}>
                            <table style={styles.table}>
                                <thead style={{background:'#2563eb', color:'#fff'}}>
                                    <tr><th>DATE</th><th>CUSTOMER</th><th>TYPE</th><th style={{textAlign:'right'}}>AMOUNT</th></tr>
                                </thead>
                                <tbody>
                                    {sales.map((s, i) => (
                                        <tr key={i} style={styles.tr}>
                                            <td>{new Date(s.sale_date).toLocaleDateString()}</td>
                                            <td><b>{s.full_name}</b></td>
                                            <td><span style={styles.badgeBlue}>{s.sale_type}</span></td>
                                            <td style={{textAlign:'right'}}><b>Rs. {Math.round(s.net_amount).toLocaleString()}</b></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {activeTab === 'inventory' && (
                        <div style={styles.tableBox}>
                            <table style={styles.table}>
                                <thead style={{background:'#10b981', color:'#fff'}}>
                                    <tr><th>DATE</th><th>ENTRY</th><th>SOURCE</th><th style={{textAlign:'right'}}>PURCHASE PRICE</th></tr>
                                </thead>
                                <tbody>
                                    {inventory.map((item, i) => (
                                        <tr key={i} style={styles.tr}>
                                            <td>{new Date(item.purchase_date).toLocaleDateString()}</td>
                                            <td><span style={styles.badgeGreen}>{item.purchase_type}</span></td>
                                            <td>{item.supplier_name || item.buyback_name || 'Stock'}</td>
                                            <td style={{textAlign:'right'}}><b>Rs. {Math.round(item.purchase_price || item.purchase_cost || 0).toLocaleString()}</b></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {activeTab === 'registration' && (
                        <div style={styles.tableBox}>
                            <table style={styles.table}>
                                <thead style={{background:'#475569', color:'#fff'}}>
                                    <tr><th>PLATE NO</th><th>ASSIGNED</th><th>RELEASED</th><th>STATUS</th></tr>
                                </thead>
                                <tbody>
                                    {registration_history.map((r, i) => (
                                        <tr key={i} style={styles.tr}>
                                            <td style={{color:'#2563eb', fontWeight:'bold'}}>{r.reg_number}</td>
                                            <td>{new Date(r.assigned_date).toLocaleDateString()}</td>
                                            <td>{r.released_date ? new Date(r.released_date).toLocaleDateString() : '-'}</td>
                                            <td>{r.is_current ? 'Current' : 'History'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const styles = {
    overlay: { position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.95)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999, backdropFilter: 'blur(8px)' },
    modal: { background: '#fff', width: '95%', maxWidth: '1100px', height: '88vh', borderRadius: '24px', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
    topHeader: { padding: '20px 40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f5f9' },
    logoBadge: { background: '#1e293b', color: '#fff', width: '40px', height: '40px', borderRadius: '10px', display: 'flex', justifyContent: 'center', alignItems: 'center', fontWeight: 'bold' },
    logo: { margin: 0, fontSize: '22px', fontWeight: '800' },
    tagline: { fontSize: '10px', color: '#64748b', fontWeight: 'bold' },
    statusStrip: { display: 'flex', gap: '50px', padding: '15px 40px', background: '#f8fafc' },
    statusItem: { display: 'flex', flexDirection: 'column', fontSize: '12px' },
    tabNav: { display: 'flex', gap: '40px', padding: '0 40px', borderBottom: '1px solid #f1f5f9' },
    tab: { padding: '18px 0', background: 'none', border: 'none', cursor: 'pointer', fontWeight: '700', fontSize: '12px' },
    scrollContent: { flex: 1, padding: '30px 40px', overflowY: 'auto' },
    grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '25px' },
    card: { padding: '20px', background: '#fff', borderRadius: '12px', border: '1px solid #f1f5f9' },
    dataRow: { display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f8fafc', fontSize: '14px' },
    tableBox: { borderRadius: '12px', overflow: 'hidden', border: '1px solid #f1f5f9' },
    table: { width: '100%', borderCollapse: 'collapse' },
    tr: { borderBottom: '1px solid #f1f5f9', fontSize: '14px' },
    badgeBlue: { background: '#eff6ff', color: '#2563eb', padding: '4px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold' },
    badgeGreen: { background: '#ecfdf5', color: '#10b981', padding: '4px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold' },
    downloadBtn: { background: '#2563eb', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' },
    exitBtn: { fontSize: '28px', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' },
    loader: { textAlign: 'center', color: '#fff' }
};

// Add global CSS for table padding
if (typeof document !== 'undefined') {
    const style = document.createElement('style');
    style.innerHTML = `th, td { padding: 15px 20px !important; } tr:hover { background: #f8fafc; }`;
    document.head.appendChild(style);
}

export default BikeLifeCycleView;